import { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import { BrowserRouter } from "react-router-dom"
import { AppRoutes } from "./app-routes"
import { RouteErrorBoundary } from "@/components/routing/route-error-boundary"
import { QueryProvider } from "@/components/providers/query-provider"
import { AuthShell } from "@/components/auth/auth-shell"
import { AgentLegalGate } from "@/components/auth/agent-legal-gate"
import { ThemeProvider } from "@/components/providers/theme-provider"
import { Toaster } from "@/components/ui/feedback/toaster"
import { StatusBarManager } from "@/components/native/status-bar-manager"
import { KeyboardViewportManager } from "@/components/native/keyboard-viewport-manager"
import { NativeStatusBarProvider } from "@/components/native/native-status-bar-context"
import { ServiceWorkerRegistration } from "@/components/native/service-worker-registration"
import { CapacitorThemePersistence } from "@/components/native/capacitor-theme-persistence"
import { StatusBarChromeProvider } from "@/components/native/status-bar-chrome-context"
import { CapacitorNativeNavigation } from "@/components/native/capacitor-native-navigation"
import { PushNotificationManager } from "@/components/native/push-notification-manager"
import { getCapacitorPlatform, IS_MOBILE_BUILD, isCapacitorNative } from "@/lib/capacitor"
import "./app/globals.css"

if (typeof document !== "undefined" && isCapacitorNative()) {
  document.documentElement.setAttribute("data-capacitor-native", "true")
  const platform = getCapacitorPlatform()
  if (platform === "ios" || platform === "android") {
    document.documentElement.setAttribute("data-capacitor-platform", platform)
  }
}

const root = document.getElementById("root")
if (!root) {
  throw new Error("Root element #root not found")
}

createRoot(root).render(
  <StrictMode>
    <QueryProvider>
      <BrowserRouter>
        <CapacitorNativeNavigation />
        <AuthShell>
          <ThemeProvider
            attribute="class"
            defaultTheme="light"
            storageKey="sigma-ui-theme"
            enableSystem={false}
          >
            <CapacitorThemePersistence />
            <StatusBarChromeProvider>
              <NativeStatusBarProvider>
                <StatusBarManager />
                <KeyboardViewportManager />
                <ServiceWorkerRegistration />
                {IS_MOBILE_BUILD && <PushNotificationManager />}
                <div
                  className={
                    IS_MOBILE_BUILD
                      ? "cap-app-shell flex h-dvh min-h-0 w-full flex-col overflow-hidden"
                      : "min-h-dvh"
                  }
                >
                  <RouteErrorBoundary>
                    <AgentLegalGate>
                      <AppRoutes />
                    </AgentLegalGate>
                  </RouteErrorBoundary>
                </div>
                <Toaster />
              </NativeStatusBarProvider>
            </StatusBarChromeProvider>
          </ThemeProvider>
        </AuthShell>
      </BrowserRouter>
    </QueryProvider>
  </StrictMode>
)
